import { EvaluationStatus } from "../common/components/questionnaireComponents/evaluateQuestionnaireDialog/EvaluateQuestionnaireDialog.types";
import { QuestionnaireAuditStatus } from "../common/components/questionnaireComponents/questionnaireStatus/QuestionnaireStatus.types";
import { RiskLevel } from "../legalEntity/legalEntityClassificationTab/LegalEntityClassificationTab.types";
import { SelectableItem } from "../processingActivity/ProcessingActivity.types";
import { BaseViewModel } from "../system/systemOverview/SystemOverview.types";
import { SegmentedControlOption } from "@common/components/segmentedControl/SegmentedControl";
import { createViewQuery } from "@common/components/viewSwitcher/viewSwitcherQuery";
import { TranslationStringType } from "@localization/LocalizationContext";

// The view switcher identifies views by numeric value; the query string persists the human-readable
// view name (?view=assessments|public|groups) while the switcher works with these values.
export enum InternalAssessmentTab {
    Assessments = 0,
    Public = 1,
    Groups = 2,
}

const queryByTab: Record<InternalAssessmentTab, string> = {
    [InternalAssessmentTab.Assessments]: "assessments",
    [InternalAssessmentTab.Public]: "public",
    [InternalAssessmentTab.Groups]: "groups",
};

const viewQuery = createViewQuery(queryByTab, InternalAssessmentTab.Assessments);

export function internalAssessmentTabToQuery(tab: InternalAssessmentTab): string {
    return viewQuery.toQuery(tab);
}

export function internalAssessmentTabFromQuery(value: string | null): InternalAssessmentTab {
    return viewQuery.fromQuery(value);
}

export function buildInternalAssessmentViews(translateString: TranslationStringType): Array<SegmentedControlOption<InternalAssessmentTab>> {
    return [
        { label: translateString("internalAssessmentTabAssessments"), value: InternalAssessmentTab.Assessments },
        { label: translateString("internalAssessmentTabGroups"), value: InternalAssessmentTab.Groups },
        { label: translateString("internalAssessmentTabPublic"), value: InternalAssessmentTab.Public },
    ];
}

export interface InternalAssessmentApiModel {
    selectableAssessmentTemplate: Array<SelectableItem>;
    selectableComplianceAreas: Array<SelectableItem>;
    selectableResponsibles: Array<SelectableItem>;
    rows: Array<InternalAssessmentApiRow>;
}

export interface InternalAssessmentApiRow extends QuestionnaireAuditStatus {
    id: string;
    name: string;
    description: string;
    uniqueIdentifier: number;
    sent: Date;
    deadline: Date;
    responsible: BaseViewModel;
    assignee: BaseViewModel;
    assigneeDeleted: boolean;
    assessmentTemplate: BaseViewModel;
    complianceAreas: Array<BaseViewModel>;
    sender: string;
    questionnaireId: string;
    questionnaireName: string;
    questionnaireResponseId: string;
    note?: string;
    riskLevel?: RiskLevel;
    score?: number;
    scoreEnabled: boolean;
    riskName: string | undefined;
    isClosed: boolean;
    canBeDeleted: boolean;
    responseExternalId: string;
    reminderSent?: Date;
    remindersPaused: boolean;
    createdFromPublicTemplate: boolean;
    publicTemplateIsDeleted: boolean;
    publicTemplateName?: string;
    internalAssessmentGroupId?: string;
    hasRecommendations: boolean;
    evaluationStatus?: EvaluationStatus;
    evaluation: string | undefined;
    remark: string;
    partOfCurrentPlan: boolean;
}

export interface InternalAssessmentViewRow extends InternalAssessmentApiRow {
    responsibleName: string;
    assigneeName: string;
    complianceAreasString: string;
    statusString: string;
    scoreName?: string;
    evaluationName?: string;
    sentString: string;
    deadlineString: string;
    reminderSentString?: string;
    notePlainText: string;
}
